(() => {
  if (window.__wavesCardModelControlsLoaded) return;
  window.__wavesCardModelControlsLoaded = true;

  const selector = 'model-media[data-waves-card-model]';
  const degreesPerPixel = 0.55;
  const defaultPitch = 180;
  const defaultYaw = 0;
  const defaultOrbitRadius = 105;
  const cueDuration = 5000;
  const cueTimers = new WeakMap();

  function hideCue(container) {
    const cue = container.querySelector('[data-waves-card-model-cue]');
    const timer = cueTimers.get(container);
    if (timer) window.clearTimeout(timer);
    cueTimers.delete(container);
    if (!cue) return;
    cue.classList.remove('is-visible');
    cue.setAttribute('aria-hidden', 'true');
  }

  function showCue(container) {
    const cue = container.querySelector('[data-waves-card-model-cue]');
    if (!cue) return;

    hideCue(container);
    // Force a fresh animation whenever the viewer is opened again.
    void cue.offsetWidth;
    cue.setAttribute('aria-hidden', 'false');
    cue.classList.add('is-visible');
    cueTimers.set(container, window.setTimeout(() => hideCue(container), cueDuration));
  }

  function initialize(container) {
    const viewer = container.querySelector('model-viewer');
    const posterUrl = container.dataset.wavesCardPoster;
    if (viewer && posterUrl) viewer.setAttribute('poster', posterUrl);
    if (!viewer || container.dataset.unrestrictedRotation === 'true') return;

    container.dataset.unrestrictedRotation = 'true';
    viewer.removeAttribute('camera-controls');
    viewer.setAttribute('tabindex', '0');
    viewer.setAttribute('aria-keyshortcuts', 'ArrowUp ArrowDown ArrowLeft ArrowRight R + -');

    const pointers = new Map();
    let pitch = defaultPitch;
    let yaw = defaultYaw;
    let zoomScale = 1;
    let pinchDistance = null;

    const updateOrientation = () => {
      viewer.orientation = `0deg ${pitch}deg ${yaw}deg`;
    };

    const updateZoom = () => {
      const radius = defaultOrbitRadius / zoomScale;
      viewer.cameraOrbit = `0deg 0deg ${radius}%`;
    };

    const resetView = () => {
      pitch = defaultPitch;
      yaw = defaultYaw;
      zoomScale = 1;
      updateOrientation();
      updateZoom();
      viewer.updateFraming?.();
      viewer.jumpCameraToGoal?.();
    };

    const distanceBetweenPointers = () => {
      const points = [...pointers.values()];
      if (points.length !== 2) return null;
      return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    };

    const zoomBy = (factor) => {
      zoomScale = Math.min(1.55, Math.max(0.7, zoomScale * factor));
      updateZoom();
    };

    const isControl = (event) => event.target instanceof Element && Boolean(event.target.closest('button, a, input, select, textarea, [role="button"]'));

    container.addEventListener('pointerdown', (event) => {
      if (!container.hasAttribute('playing') || isControl(event)) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      hideCue(container);
      event.preventDefault();
      event.stopImmediatePropagation();
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) pinchDistance = distanceBetweenPointers();
      container.setPointerCapture?.(event.pointerId);
      viewer.focus({ preventScroll: true });
    }, true);

    container.addEventListener('pointermove', (event) => {
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size === 1) {
        yaw += (event.clientX - previous.x) * degreesPerPixel;
        pitch += (event.clientY - previous.y) * degreesPerPixel;
        updateOrientation();
      }
      else if (pointers.size === 2) {
        const nextDistance = distanceBetweenPointers();
        if (pinchDistance && nextDistance) zoomBy(nextDistance / pinchDistance);
        pinchDistance = nextDistance;
      }
    }, true);

    const endPointer = (event) => {
      if (!pointers.has(event.pointerId)) return;
      event.stopImmediatePropagation();
      pointers.delete(event.pointerId);
      pinchDistance = pointers.size === 2 ? distanceBetweenPointers() : null;
    };

    container.addEventListener('pointerup', endPointer, true);
    container.addEventListener('pointercancel', endPointer, true);
    container.addEventListener('lostpointercapture', endPointer, true);

    container.addEventListener('wheel', (event) => {
      if (!container.hasAttribute('playing') || isControl(event)) return;
      hideCue(container);
      event.preventDefault();
      event.stopImmediatePropagation();
      zoomBy(Math.exp(-event.deltaY / 900));
    }, { passive: false, capture: true });

    viewer.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 30 : 10;
      if (event.key === 'ArrowUp') pitch -= step;
      else if (event.key === 'ArrowDown') pitch += step;
      else if (event.key === 'ArrowLeft') yaw -= step;
      else if (event.key === 'ArrowRight') yaw += step;
      else if (event.key.toLowerCase() === 'r') {
        hideCue(container);
        return resetView();
      }
      else if (event.key === '+' || event.key === '=') {
        hideCue(container);
        return zoomBy(1.12);
      }
      else if (event.key === '-' || event.key === '_') {
        hideCue(container);
        return zoomBy(1 / 1.12);
      }
      else return;

      hideCue(container);
      event.preventDefault();
      event.stopImmediatePropagation();
      updateOrientation();
    }, true);

    container.addEventListener('dblclick', (event) => {
      if (!container.hasAttribute('playing') || isControl(event)) return;
      hideCue(container);
      event.preventDefault();
      resetView();
    });

    resetView();
  }

  function initializeAll(root = document) {
    if (root instanceof Element && root.matches(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  }

  customElements.whenDefined('model-viewer').then(() => initializeAll());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('modelViewer:play', () => {
    initializeAll();
    document.querySelectorAll(`${selector}[playing]`).forEach(showCue);
  });
  document.addEventListener('modelViewer:pause', () => {
    document.querySelectorAll(selector).forEach(hideCue);
  });
})();
